import { mmss, usd, ptTime } from "@/lib/api";

const COINS = ["BTC", "ETH", "SOL"];
const DEC = { BTC: 2, ETH: 2, SOL: 3 };

function Coin({ a, s }) {
  const c = s.config;
  const d = s.deltas?.[a] || {};
  const thr = c.velocity_threshold[a];
  const hot = d.delta != null && Math.abs(d.delta) >= thr;
  const on = c.active_markets[a];
  return (
    <div data-testid={`coin-${a.toLowerCase()}`} className={`rounded-lg border p-3 ${on ? "border-[var(--border-active)]" : "border-[var(--border-subtle)] opacity-60"}`}>
      <div className="flex items-center justify-between">
        <span className="font-head font-bold">{a}</span>
        <span data-testid={`coin-${a.toLowerCase()}-toggle-state`} className={`text-[10px] uppercase tracking-widest ${on ? "text-[var(--buy)]" : "text-[var(--text-mute)]"}`}>{on ? "ON" : "OFF"}</span>
      </div>
      <div className="font-mono-num text-lg mt-1">{d.price != null ? `$${Number(d.price).toFixed(DEC[a])}` : "—"}</div>
      <div data-testid={`coin-${a.toLowerCase()}-delta`} className={`font-mono-num text-sm ${hot ? (d.delta > 0 ? "text-[var(--buy)]" : "text-[var(--sell)]") : "text-[var(--text-dim)]"}`}>
        {d.delta != null ? `${d.delta > 0 ? "+" : ""}${Number(d.delta).toFixed(DEC[a] + 1)}` : "—"} in {d.window ?? "—"}s · need ±${thr}
      </div>
      <div className="text-[11px] text-[var(--text-mute)] mt-1 truncate">{s.markets?.[a]?.ticker || "no market yet"} · max {c.max_entry_cents[a]}¢ · ≥{c.min_contract_depth[a]} contracts</div>
    </div>
  );
}

function Stat({ label, value, testid, tone = "" }) {
  return (
    <div className="rounded-lg border border-[var(--border-subtle)] px-3 py-2">
      <div className="text-[10px] uppercase tracking-widest text-[var(--text-mute)]">{label}</div>
      <div data-testid={testid} className={`font-mono-num text-base ${tone}`}>{value}</div>
    </div>
  );
}

export default function LiveStatus({ s }) {
  const c = s.config;
  const pnl = s.today_pnl_usd;
  return (
    <section data-testid="live-status" className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <Stat label="Cycle clock" testid="cycle-clock" value={`${mmss(s.cycle_elapsed_sec)} / scan ${c.start_scan_window}–${c.end_scan_window}`} />
        <Stat label="Trades this cycle" testid="trades-this-cycle" value={`${s.trades_this_cycle} / ${c.trades_per_cycle}`} />
        <Stat label="Open trades" testid="open-trades-count" value={`${s.open_trades.length} / ${c.max_concurrent_trades}`} />
        <Stat label="Wager per trade" testid="per-trade" value={usd(s.per_trade_usd)} />
        <Stat label="Today (Pacific)" testid="today-pnl" value={`${usd(pnl)} / cap ${usd(-c.daily_loss_cap_usd)}`}
          tone={pnl > 0 ? "text-[var(--buy)]" : pnl < 0 ? "text-[var(--sell)]" : ""} />
        <Stat label="Feeds" testid="feeds" value={Object.entries(s.feeds).map(([k, v]) => `${k === "coinbase" ? "CB" : "BUS"} ${v}`).join(" · ")} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">{COINS.map((a) => <Coin key={a} a={a} s={s} />)}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs font-mono-num">
        <div data-testid="last-trigger" className="rounded-lg border border-[var(--border-subtle)] px-3 py-2 text-[var(--text-dim)]">
          Last trigger: {s.last_trigger ? `${ptTime(s.last_trigger.ts)} · ${s.last_trigger.asset} ${s.last_trigger.direction} ${s.last_trigger.delta > 0 ? "+" : ""}${s.last_trigger.delta} in ${s.last_trigger.window}s` : "none yet"}
        </div>
        <div data-testid="last-skip" className="rounded-lg border border-[var(--border-subtle)] px-3 py-2 text-[var(--text-dim)]">
          Last skip: {s.last_skip ? `${ptTime(s.last_skip.ts)} · ${s.last_skip.asset}: ${s.last_skip.reason}` : "none yet"}
        </div>
      </div>
      {s.open_trades.length > 0 && (
        <div data-testid="open-trades" className="rounded-lg border border-cyan-800 px-3 py-2 text-xs font-mono-num space-y-1">
          {s.open_trades.map((t) => (
            <div key={t.id} data-testid={`open-trade-${t.id}`}>{t.ticker} {t.side.toUpperCase()} {t.qty}@{t.entry_cents}¢ → target {t.tp_cents}¢ · opened {ptTime(t.opened_at)}</div>
          ))}
        </div>
      )}
    </section>
  );
}
